import { useState, useMemo } from "react";
import { Box, Typography, Paper, Grid, TextField, Divider, Chip, Alert, Stack } from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { useResponsive } from "../../utils/mobileresponsive";
import FloatingSupportButton from "../../components/FloatingSupportButton";
import { isFieldAgent } from "../../utils/roleDetection";

const RATES = { l1: 0.5, l2: 0.3, l3: 0.2 };

const fmt = (v) => v.toLocaleString("en-US", { style: "currency", currency: "USD" });

const levelMeta = [
	{ key: "l1", label: "Level 1", rate: RATES.l1, color: "#00838F", accentColor: "#00ACC1", bg: "#E0F7FA", borderColor: "#00ACC1" },
	{ key: "l2", label: "Level 2", rate: RATES.l2, color: "#1565C0", accentColor: "#1D54A2", bg: "#E3F2FD", borderColor: "#1E88E5" },
	{ key: "l3", label: "Level 3", rate: RATES.l3, color: "#6A1B9A", accentColor: "#8E24AA", bg: "#F3E5F5", borderColor: "#8E24AA" },
];

const MetricCard = ({ label, value, sub }) => (
	<Box
		sx={{
			bgcolor: "#F8FAFD",
			border: "1px solid #E3ECF7",
			borderRadius: 2,
			p: 1.75,
			flex: 1,
			minWidth: { xs: "calc(50% - 6px)", sm: 120 },
			boxShadow: "0 1px 4px rgba(29,84,162,0.06)",
		}}
	>
		<Typography
			variant="caption"
			sx={{ color: "#6B82A0", display: "block", mb: 0.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}
		>
			{label}
		</Typography>
		<Typography variant="h6" sx={{ fontWeight: 700, color: "#1D54A2", lineHeight: 1.2 }}>
			{value}
		</Typography>
		{sub && (
			<Typography variant="caption" sx={{ color: "#AAB8C8" }}>
				{sub}
			</Typography>
		)}
	</Box>
);

const LevelInput = ({ meta, agents, members, onAgentsChange, onMembersChange }) => (
	<Box
		sx={{
			borderLeft: `3px solid ${meta.borderColor}`,
			pl: 2,
			py: 0.5,
			height: "100%",
			minWidth: 0,
		}}
	>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
			<Typography variant="body2" fontWeight={700} sx={{ color: "#1D2D3E" }}>
				{meta.label}
			</Typography>
			<Chip
				label={`$${meta.rate.toFixed(2)}/member`}
				size="small"
				sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: 11, height: 20 }}
			/>
		</Box>
		<TextField
			label="Number of agents"
			type="number"
			fullWidth
			size="small"
			value={agents}
			onChange={(e) => onAgentsChange(Math.max(0, parseInt(e.target.value) || 0))}
			inputProps={{ min: 0, step: 1 }}
			sx={{
				mb: 1.5,
				"& .MuiOutlinedInput-root": {
					bgcolor: "#F8FAFD",
					"& fieldset": { borderColor: "#DDE6F0" },
					"&:hover fieldset": { borderColor: meta.accentColor },
					"&.Mui-focused fieldset": { borderColor: meta.accentColor },
				},
				"& .MuiInputLabel-root.Mui-focused": { color: meta.accentColor },
				"& .MuiInputBase-input": { border: "none !important" },
			}}
		/>
		<TextField
			label="Avg members each"
			type="number"
			fullWidth
			size="small"
			value={members}
			onChange={(e) => onMembersChange(Math.max(0, parseInt(e.target.value) || 0))}
			inputProps={{ min: 0, step: 1 }}
			sx={{
				"& .MuiOutlinedInput-root": {
					bgcolor: "#F8FAFD",
					"& fieldset": { borderColor: "#DDE6F0" },
					"&:hover fieldset": { borderColor: meta.accentColor },
					"&.Mui-focused fieldset": { borderColor: meta.accentColor },
				},
				"& .MuiInputLabel-root.Mui-focused": { color: meta.accentColor },
				"& .MuiInputBase-input": { border: "none !important" },
			}}
		/>
	</Box>
);

const BreakdownRow = ({ meta, agents, members }) => {
	const earn = agents * members * meta.rate;
	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				px: 1.5,
				py: 1.25,
				borderRadius: 1.5,
				mb: 0.5,
				bgcolor: earn > 0 ? `${meta.bg}60` : "transparent",
				border: "1px solid",
				borderColor: earn > 0 ? `${meta.borderColor}30` : "transparent",
				transition: "background 0.2s",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
				<Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: 11, height: 20 }} />
				<Typography variant="body2" sx={{ color: "#5A7090", fontWeight: 500, fontSize: { xs: 12, sm: 14 } }}>
					{agents} agent{agents !== 1 ? "s" : ""} × {members} members
				</Typography>
			</Box>
			<Typography variant="body2" fontWeight={700} sx={{ color: earn > 0 ? meta.accentColor : "#AAB8C8", whiteSpace: "nowrap", ml: 1 }}>
				{fmt(earn)}
			</Typography>
		</Box>
	);
};

const CommissionTool = () => {
	const stored = localStorage.getItem("loggedin_data");
	const user = stored ? JSON.parse(stored) : null;
	const roles = Array.isArray(user?.roles) ? user.roles : [];
	const isAgent = isFieldAgent(roles);
	const [inputs, setInputs] = useState({
		l1Agents: 3,
		l1Members: 100,
		l2Agents: 0,
		l2Members: 0,
		l3Agents: 0,
		l3Members: 0,
	});
	const { isMobile } = useResponsive();

	const set = (key) => (val) => setInputs((prev) => ({ ...prev, [key]: val }));

	const totals = useMemo(() => {
		const l1earn = inputs.l1Agents * inputs.l1Members * RATES.l1;
		const l2earn = inputs.l2Agents * inputs.l2Members * RATES.l2;
		const l3earn = inputs.l3Agents * inputs.l3Members * RATES.l3;
		const monthly = l1earn + l2earn + l3earn;
		const totalAgents = inputs.l1Agents + inputs.l2Agents + inputs.l3Agents;
		const totalMembers = inputs.l1Agents * inputs.l1Members + inputs.l2Agents * inputs.l2Members + inputs.l3Agents * inputs.l3Members;
		return { l1earn, l2earn, l3earn, monthly, annual: monthly * 12, totalAgents, totalMembers };
	}, [inputs]);

	return (
		<Box sx={{ p: { xs: 2, sm: 4 } }}>
			{/* Header */}
			<Typography
				variant={isMobile ? "h5" : "h4"}
				sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: { xs: 2, sm: 4 }, pl: { xs: 0, sm: 2 } }}
			>
				COMMISSION SIMULATION TOOL
			</Typography>
			<Box my={2}>
				<Divider />
			</Box>

			<Alert
				icon={<LightbulbOutlinedIcon fontSize="small" />}
				severity="info"
				sx={{
					ml: { xs: 0, sm: 1 },
					mb: 2,
					bgcolor: "#EBF5FF",
					color: "#1D54A2",
					"& .MuiAlert-icon": { color: "#1D54A2" },
					border: "1px solid #C5DCF7",
					fontSize: { xs: 12, sm: 14 },
				}}
			>
				Estimate your monthly override earnings by entering how many agents you plan to recruit at each level and their expected member
				counts.
			</Alert>

			{/* Single container */}
			<Paper
				elevation={0}
				sx={{
					position: "relative",
					overflow: "hidden",
					bgcolor: "#fff",
					border: "2px solid #E3ECF7",
					borderRadius: "12px",
					boxShadow: "0 2px 12px rgba(29,84,162,0.07), 0 0 1px rgba(29,84,162,0.08)",
				}}
			>
				{/* Watermark icon */}
				<Box
					sx={{
						position: "absolute",
						top: -10,
						right: -20,
						opacity: 0.035,
						pointerEvents: "none",
						zIndex: 999,
					}}
				>
					<RequestQuoteOutlinedIcon sx={{ fontSize: { xs: 150, sm: 250 }, color: "#1D54A2" }} />
				</Box>

				{/* All content above watermark */}
				<Box sx={{ position: "relative", zIndex: 1 }}>
					{/* Input section */}
					<Box sx={{ p: { xs: 2, sm: 2.5 }, pb: 2 }}>
						<Typography
							variant="caption"
							sx={{ fontWeight: 700, color: "#8FA3B8", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5, display: "block" }}
						>
							Forecasted Recruits
						</Typography>
						<Grid container spacing={2} sx={{ mb: 1 }} wrap={isMobile ? "wrap" : "nowrap"}>
							{levelMeta.map((meta, i) => (
								<Grid item xs={12} md={4} key={meta.key}>
									{/* Divider between stacked items on mobile */}
									{isMobile && i > 0 && <Divider sx={{ borderColor: "#EEF2F7", mb: 2 }} />}
									<LevelInput
										meta={meta}
										agents={inputs[`${meta.key}Agents`]}
										members={inputs[`${meta.key}Members`]}
										onAgentsChange={set(`${meta.key}Agents`)}
										onMembersChange={set(`${meta.key}Members`)}
									/>
								</Grid>
							))}
						</Grid>
					</Box>

					<Divider sx={{ borderColor: "#EEF2F7" }} />

					{/* Summary metrics */}
					<Box sx={{ p: { xs: 2, sm: 2.5 }, pb: 2 }}>
						<Typography
							variant="caption"
							sx={{ fontWeight: 700, color: "#8FA3B8", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5, display: "block" }}
						>
							Projected Summary
						</Typography>
						<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
							<MetricCard label="Total agents recruited" value={totals.totalAgents.toLocaleString()} sub="across all levels" />
							<MetricCard label="Total downline members" value={totals.totalMembers.toLocaleString()} sub="active members" />
							<MetricCard label="Est. monthly override" value={fmt(totals.monthly)} sub="per month" />
							<MetricCard label="Est. annual override" value={fmt(totals.annual)} sub="projected" />
						</Box>
					</Box>

					<Divider sx={{ borderColor: "#EEF2F7" }} />

					{/* Per-level breakdown */}
					<Box sx={{ p: { xs: 2, sm: 2.5 }, pb: 2 }}>
						<Typography
							variant="caption"
							sx={{ fontWeight: 700, color: "#8FA3B8", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5, display: "block" }}
						>
							Earnings Breakdown
						</Typography>
						{levelMeta.map((meta) => (
							<BreakdownRow key={meta.key} meta={meta} agents={inputs[`${meta.key}Agents`]} members={inputs[`${meta.key}Members`]} />
						))}
					</Box>

					{/* Total footer */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: 1.5,
							backgroundColor: "#1D54A2",
							px: { xs: 2, sm: 2.5 },
							py: 2,
						}}
					>
						<Box>
							<Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: "0.08em", textTransform: "uppercase" }}>
								Total Monthly Override
							</Typography>
							<Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)", mt: 0.25 }}>Est. annual: {fmt(totals.annual)}</Typography>
						</Box>
						<Box
							sx={{
								backgroundColor: "rgba(255,255,255,0.15)",
								borderRadius: 1.5,
								px: { xs: 2, sm: 3 },
								py: 0.75,
							}}
						>
							<Typography
								sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 700, color: "#fff", letterSpacing: "0.06em", lineHeight: 1.1 }}
							>
								{fmt(totals.monthly)}
							</Typography>
						</Box>
					</Box>

					{/* Footer note */}
					<Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.25, bgcolor: "#F8FAFD", borderTop: "1px solid #EEF2F7" }}>
						<Typography variant="caption" sx={{ color: "#AAB8C8", lineHeight: 1.6 }}>
							Override rates: L1 $0.50 · L2 $0.30 · L3 $0.20 per active member/month. Overrides stop after Level 3. Projections are
							estimates based on inputted values.
						</Typography>
					</Box>
				</Box>
			</Paper>
			{isAgent && <FloatingSupportButton />}
		</Box>
	);
};

export default CommissionTool;
